import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, WhatsappLogo, Sparkle, Bed, ShieldCheck, Key, Drop } from "@phosphor-icons/react";
import { MaskedLines, Reveal } from "../components/Reveal";
import { ContactCTA } from "../components/ContactCTA";
import { SERVICES, ZONES, whatsappLink } from "../lib/data";

const ICONS = { pulizie: Sparkle, biancheria: Bed, sanificazione: ShieldCheck, checkin: Key, lavanderia: Drop };

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex flex-col justify-end overflow-hidden">
        <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1649429710616-dad56ce9a076?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
            alt="Interno luminoso di una casa vacanza a Roma"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-background/5" />
          <div className="absolute inset-0 bg-gradient-to-r from-background/40 to-transparent" />
        </motion.div>

        <div className="relative z-10 max-w-[1400px] w-full mx-auto px-6 lg:px-10 pb-20 pt-40">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            className="overline text-secondary mb-8 flex items-center gap-3">
            <span className="w-10 h-px bg-secondary inline-block" /> Case Vacanza · Roma
          </motion.div>

          <h1 className="serif text-primary font-light tracking-tighter leading-[0.92] text-5xl sm:text-7xl lg:text-[6.5rem]">
            <MaskedLines lines={["L'eccellenza", "dell'ospitalità", "romana."]} delay={0.35} />
          </h1>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.9 }}
            className="mt-10 flex flex-col lg:flex-row lg:items-end justify-between gap-8">
            <p className="text-lg font-light text-muted-foreground max-w-md leading-relaxed">
              Pulizie professionali e noleggio biancheria per la tua casa vacanza. Turnover impeccabili, ospiti soddisfatti, zero pensieri.
            </p>
            <div className="flex gap-4">
              <a href={whatsappLink()} target="_blank" rel="noreferrer" data-testid="hero-whatsapp-btn"
                 className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-7 py-4 hover:bg-primary/90 transition-colors active:scale-95">
                <WhatsappLogo size={20} weight="fill" /> Richiedi preventivo
              </a>
              <Link to="/servizi" data-testid="hero-services-btn"
                    className="inline-flex items-center gap-2 rounded-full border border-primary/25 text-primary px-7 py-4 hover:bg-primary hover:text-primary-foreground transition-colors">
                Servizi <ArrowRight size={18} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="border-y border-border/50 py-6 bg-background">
        <Marquee speed={38} gradient={false} autoFill>
          {["Pulizie Case Vacanza", "Noleggio Biancheria", "Sanificazione", "Check-in & Check-out", "Gestione Lavanderia"].map((t, i) => (
            <span key={i} className="serif text-3xl md:text-4xl font-light text-primary/80 italic mx-10 flex items-center gap-10">
              {t} <span className="text-secondary not-italic">✦</span>
            </span>
          ))}
        </Marquee>
      </div>

      {/* SERVICES BENTO */}
      <section data-testid="services-section" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-28 md:py-36">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
          <Reveal>
            <div className="overline text-secondary mb-5">Cosa facciamo</div>
            <h2 className="serif text-4xl md:text-6xl font-light tracking-tighter text-primary max-w-xl leading-none">
              Un servizio completo per host esigenti.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Link to="/servizi" className="inline-flex items-center gap-2 text-primary border-b border-primary/30 pb-1 hover:gap-4 transition-all">
              Tutti i servizi <ArrowRight size={18} />
            </Link>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.id] || Sparkle;
            const spans = ["md:col-span-7", "md:col-span-5", "md:col-span-5", "md:col-span-7", "md:col-span-12"];
            const isFeatured = i === 0 || i === 3;
            return (
              <Reveal key={s.id} delay={i * 0.06} className={spans[i]}>
                <div data-testid={`service-card-${s.id}`}
                     className="group relative h-full overflow-hidden bg-card clip-tl min-h-[280px] flex flex-col justify-between p-8 md:p-10 transition-colors hover:bg-primary hover:text-primary-foreground">
                  {isFeatured && (
                    <img src={s.img} alt={s.title}
                         className="absolute inset-0 w-full h-full object-cover opacity-25 group-hover:opacity-40 group-hover:scale-105 transition-all duration-700 mix-blend-multiply" />
                  )}
                  <div className="relative flex items-start justify-between">
                    <span className="serif text-5xl font-light text-secondary">{s.num}</span>
                    <Icon size={34} weight="thin" className="text-primary group-hover:text-primary-foreground transition-colors" />
                  </div>
                  <div className="relative mt-16">
                    <h3 className="serif text-3xl md:text-4xl font-light tracking-tight mb-3">{s.title}</h3>
                    <p className="font-light text-muted-foreground group-hover:text-primary-foreground/70 max-w-sm leading-relaxed transition-colors">
                      {s.short}
                    </p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* MANIFESTO / HOW IT WORKS */}
      <section data-testid="manifesto-section" className="bg-primary text-primary-foreground">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-28 md:py-40">
          <Reveal>
            <div className="overline text-secondary mb-6">Come lavoriamo</div>
            <h2 className="serif text-4xl md:text-6xl font-light tracking-tighter max-w-2xl leading-none mb-20">
              Tre passi verso un turnover perfetto.
            </h2>
          </Reveal>
          {[
            { n: "01", t: "Ci contatti", d: "Un messaggio su WhatsApp con i dettagli del tuo immobile e delle date. Ti inviamo un preventivo chiaro e senza sorprese." },
            { n: "02", t: "Prepariamo tutto", d: "Coordiniamo pulizie, biancheria fresca e sanificazione in base al calendario dei tuoi ospiti. Ci pensiamo noi, dall'inizio alla fine." },
            { n: "03", t: "Accogli gli ospiti", d: "Casa impeccabile, letti rifatti, tutto al suo posto. Tu ricevi solo recensioni a cinque stelle." },
          ].map((step, i) => (
            <Reveal key={step.n} delay={i * 0.08}>
              <div className={`grid md:grid-cols-12 gap-6 items-baseline py-10 border-t border-primary-foreground/15 ${i === 2 ? "border-b" : ""}`}>
                <div className="md:col-span-2 serif text-6xl md:text-7xl font-light text-secondary">{step.n}</div>
                <h3 className="md:col-span-4 serif text-3xl md:text-4xl font-light">{step.t}</h3>
                <p className="md:col-span-6 font-light text-primary-foreground/60 text-lg leading-relaxed">{step.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* COVERAGE */}
      <section data-testid="coverage-section" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-28 md:py-36">
        <div className="grid md:grid-cols-12 gap-12 items-start">
          <Reveal className="md:col-span-5">
            <div className="overline text-secondary mb-6">Dove operiamo</div>
            <h2 className="serif text-4xl md:text-6xl font-light tracking-tighter text-primary leading-none">
              Tutta Roma, quartiere per quartiere.
            </h2>
            <p className="mt-8 font-light text-muted-foreground text-lg leading-relaxed max-w-md">
              Dal Centro Storico all'EUR, copriamo le principali zone turistiche della capitale con squadre puntuali e affidabili.
            </p>
          </Reveal>
          <Reveal delay={0.15} className="md:col-span-7">
            <div className="flex flex-wrap gap-3">
              {ZONES.map((z, i) => (
                <span key={z} data-testid={`zone-${i}`}
                      className="rounded-full border border-border px-6 py-3 font-light text-primary hover:bg-primary hover:text-primary-foreground transition-colors cursor-default">
                  {z}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <ContactCTA />
    </div>
  );
}
